<template>
  <div class="image-block" data-cy="image-block">
    <div class="mt-4">
      <template v-if="hasImage">
        <div class="flex flex-col items-center gap-3">
          <div
            class="group relative h-48 overflow-auto rounded-md border border-gray-200"
            data-cy="image-block-image-preview"
          >
            <img
              v-if="imageUrl"
              :src="imageUrl"
              class="h-full w-full object-contain"
              data-cy="image-block-image"
            />
            <div
              class="absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            >
              <button
                v-if="!disabled"
                type="button"
                class="bg-opacity-80 hover:bg-opacity-100 absolute top-2 left-2 flex items-center gap-1 rounded-full bg-gray-200 px-3 py-1 text-xs text-gray-600 transition-colors"
                data-cy="image-block-edit-btn"
                @click="() => openFileDialog()"
              >
                {{ $t("COMMON.EDIT") }}
              </button>
              <button
                v-if="!disabled"
                type="button"
                class="bg-opacity-80 hover:bg-opacity-100 absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-gray-600 transition-colors"
                data-cy="image-block-delete-btn"
                @click="handleDeleteImage"
              >
                <icon-base
                  :icon="IconX"
                  width="12"
                  height="12"
                  class="-mt-0.5"
                />
              </button>
            </div>
          </div>

          <div
            v-if="!disabled"
            class="flex w-full items-start justify-between gap-4"
          >
            <div class="w-26">
              <lf-input
                v-if="!isFullWidth"
                :value="scale"
                type="number"
                :min="1"
                :max="100"
                :step="1"
                :placeholder="$t('BORROWER_PLATFORM.SCALE_PERCENT')"
                :disabled="disabled"
                name="scale-input"
                data-cy="scale-input"
                @keydown="handleScaleKeydown"
                @key-released="handleScaleChange($event, 1, 100, 1)"
              />
              <lf-input
                v-else
                value=""
                :disabled="true"
                class="pointer-events-none invisible"
                name="placeholder-input"
              />
            </div>

            <div class="mt-2 flex items-center space-x-2">
              <span>
                {{ $t("BORROWER_PLATFORM.FULL_WIDTH") }}
              </span>
              <lf-switch
                :model-value="isFullWidth"
                :disabled="disabled"
                name="full-width-toggle"
                data-cy="full-width-toggle"
                @update:model-value="handleFullWidthToggle"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <upload-box
          v-if="!disabled"
          accept=".png,.gif,.jpeg,.jpg"
          data-cy="image-block-upload-box"
          @files:add="handleFilesAdd"
        />
        <div
          v-else
          class="flex h-32 items-center justify-center rounded-md border-2 border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500"
        >
          {{ $t("BORROWER_PLATFORM.NO_IMAGE_SELECTED") }}
        </div>
      </template>
    </div>

    <lf-modal
      v-if="imageState.showEditModal"
      :close="handleModalCancel"
      data-cy="image-block-edit-modal"
    >
      <template #content>
        <div class="flex flex-col items-center gap-4">
          <avatar-editor
            v-if="imageState.pendingFile"
            :avatar="imageState.pendingFile"
            size="large"
            :preview="false"
            data-cy="image-block-avatar-editor"
            @save="handleImageSave"
            @cancel="handleModalCancel"
          />
        </div>
      </template>
    </lf-modal>
  </div>
</template>

<script setup lang="ts">
import { useFileDialog } from "@vueuse/core";
import { computed, ref } from "vue";
import { toRef } from "vue";

import { useBlockEdit } from "@/composables/useBlockEdit";
import { useDimensions } from "@/composables/useDimensions";
import { useQueuedFileDeletion } from "@/composables/useQueuedFileDeletion";
import { handleScaleKeydown } from "@/helpers/inputValidation";
import {
  useBorrowerPlatformTemplateFileQuery,
  useUploadBorrowerPlatformTemplateFileMutation,
} from "@/hooks/queries/useBorrowerPlatformTemplates";

import IconX from "@/components/icons/deals/IconX.vue";
import IconBase from "@/components/ui/IconBase.vue";
import AvatarEditor from "@/components/ui/ImageEditor.vue";
import LfModal from "@/components/ui/Modal.vue";
import LfInput from "@/components/ui/inputs/LfInput.vue";
import LfSwitch from "@/components/ui/inputs/LfSwitch.vue";
import UploadBox from "@/components/ui/inputs/UploadBox.vue";

import type { ImageBlockProps } from "@/models/borrowerPlatformTemplates";

interface ImageState {
  pendingFile?: File;
  showEditModal: boolean;
  currentUrl: string;
}

const {
  templateId,
  modelValue,
  disabled = false,
} = defineProps<
  ImageBlockProps & {
    disabled?: boolean;
  }
>();

const { updateMultipleFields } = useBlockEdit(toRef(() => modelValue));

const { isFullWidth, scale, handleFullWidthToggle, handleScaleChange } =
  useDimensions(
    toRef(() => modelValue),
    updateMultipleFields,
    100
  );

const imageState = ref<ImageState>({
  pendingFile: undefined,
  showEditModal: false,
  currentUrl: "",
});

const { open: openFileDialog, onChange } = useFileDialog({
  accept: "image/*",
  directory: false,
});

const uploadFileMutation = useUploadBorrowerPlatformTemplateFileMutation();
const { queueFileDeletion } = useQueuedFileDeletion();

const fileId = computed(() => String(modelValue.file_id ?? ""));

const { data: fileData } = useBorrowerPlatformTemplateFileQuery(
  templateId,
  fileId
);

const hasImage = computed(() => !!modelValue.file_id);

const imageUrl = computed(() => {
  if (!modelValue.file_id) {
    return "";
  }
  return (
    modelValue.file_url ??
    fileData.value?.file_url ??
    imageState.value.currentUrl
  );
});

const handleFilesAdd = (files: FileList) => {
  if (!files?.length) {
    return;
  }

  imageState.value = {
    ...imageState.value,
    pendingFile: files[0],
    showEditModal: true,
  };
};

onChange((files) => {
  if (files?.length) {
    handleFilesAdd(files);
  }
});

const handleImageSave = async (croppedFile: File) => {
  const uploadResult = await uploadFileMutation.mutateAsync({
    templateId,
    file: croppedFile,
  });

  updateMultipleFields({
    file_id: Number(uploadResult.id),
    file_url: uploadResult.file_url,
  });

  imageState.value = {
    ...imageState.value,
    showEditModal: false,
    pendingFile: undefined,
  };
};

const handleModalCancel = () => {
  imageState.value = {
    ...imageState.value,
    showEditModal: false,
    pendingFile: undefined,
  };
};

const handleDeleteImage = () => {
  if (!modelValue.file_id) {
    return;
  }

  queueFileDeletion(templateId, String(modelValue.file_id));
  updateMultipleFields({ file_id: undefined, file_url: undefined });
  imageState.value.currentUrl = "";
};
</script>
